'use client';

/**
 * DraftAppPanel — the manage view for an UNPUBLISHED (private) app.
 *
 * The published counterpart is the app's channel page (ChannelPanelView /
 * `/c/<slug>`), which carries the community feed and the owner Manage drawer.
 * A draft has no channel yet, so this panel is its "store page": the shared
 * AppStoreHeader (identical layout to the published page) plus the owner
 * actions every surface must offer — Publish, Share, and the Manage drawer
 * (name, description, icon, cover, thumbnail: DraftManageDrawer, the draft
 * twin of manage-app.tsx) — and the channel explainer. Rendered by the Apps
 * panel drill-down AND the Discover card drill-down so the two cannot drift;
 * surface-specific buttons (Open on the canvas, Edit source) come in through
 * `actions`.
 *
 * Identity edits land on the widget content row's metadata, which is what
 * the catalog cards read and what publish seeds the listing channel from —
 * so branding done here follows the app into the marketplace.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { AppWindow, MessagesSquare, Pencil, Rocket, Share2 } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { coverGradient } from '@/lib/marketplace/cover';
import { appMarkSeed, widgetIconRef } from '@/lib/content/app-identity';
import { lucideMark } from '@/components/workspace/panels/content-preview';
import { emitWorkspaceItemsChanged } from '@/lib/workspaces/list-events';
import { openPublishAppDialog } from '@/components/workspace/publish-app-dialog';
import { ShareDialog } from '@/components/sharing/share-dialog';
import { AppStoreHeader } from '@/components/marketplace/app-store-header';
import { DraftManageDrawer, type DraftIdentity } from '@/components/marketplace/draft-manage-drawer';
import { RemixWithDialog } from '@/components/workspace/remix-with-dialog';

export function DraftAppPanel({
  contentId,
  name,
  description,
  metadata,
  autoOpenManage,
  actions,
}: {
  /** The app's widget content row (the id sharing and publishing act on). */
  contentId: string;
  name: string;
  description?: string | null;
  /** The widget row's metadata — icon / color / cover / thumbnail source. */
  metadata?: Record<string, unknown> | null;
  /** Land with the Manage drawer already open (context-menu "Manage"). */
  autoOpenManage?: boolean;
  /** Surface-specific buttons rendered after the shared owner actions. */
  actions?: ReactNode;
}) {
  const t = useT();
  const [shareOpen, setShareOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  // Live identity: seeded from the row, updated in place by the Manage
  // drawer's save so the header repaints without a refetch.
  const [identity, setIdentity] = useState<DraftIdentity>(() => {
    const meta = metadata ?? null;
    const iconRef = widgetIconRef(meta);
    return {
      name: name || 'Untitled app',
      description: description ?? ((meta?.description as string) || null),
      icon: iconRef?.kind === 'lucide' ? iconRef.name : null,
      color: typeof meta?.color === 'string' ? (meta.color as string) : null,
      bannerUrl: typeof meta?.bannerUrl === 'string' ? (meta.bannerUrl as string) : null,
      logoUrl: typeof meta?.logoUrl === 'string' ? (meta.logoUrl as string) : null,
    };
  });

  const seed = appMarkSeed(metadata, contentId);
  const coverCss = coverGradient(seed, identity.color).css;

  // Mark chip: the picked lucide glyph, else the manifest's own art (URL
  // icons render as the chip image), else the generic app window.
  const { markIcon, markLogoUrl } = useMemo(() => {
    const picked = identity.icon ? lucideMark({ kind: 'lucide', name: identity.icon }) : null;
    if (picked) return { markIcon: picked, markLogoUrl: identity.logoUrl };
    const ref = widgetIconRef(metadata ?? null);
    if (ref?.kind === 'url') return { markIcon: null, markLogoUrl: identity.logoUrl ?? ref.url };
    const manifest = lucideMark(ref);
    return { markIcon: manifest, markLogoUrl: identity.logoUrl };
  }, [identity.icon, identity.logoUrl, metadata]);
  const MarkIcon = markIcon;

  return (
    <>
      <AppStoreHeader
        coverCss={coverCss}
        bannerUrl={identity.bannerUrl}
        logoUrl={markLogoUrl}
        name={identity.name}
        pathLabel="Draft · not published yet"
        description={identity.description}
        fallbackIcon={MarkIcon ? <MarkIcon className="h-6 w-6" /> : <AppWindow className="h-6 w-6" />}
      >
        <button
          type="button"
          onClick={() =>
            openPublishAppDialog({
              contentId,
              name: identity.name,
              description: identity.description || undefined,
            })
          }
          className="inline-flex items-center gap-1.5 rounded-lg bg-foreground px-4 py-2 text-sm font-medium text-background transition-opacity hover:opacity-90"
        >
          <Rocket className="h-4 w-4" />
          {t('workspace.publish.publish')}
        </button>
        <button
          type="button"
          onClick={() => setShareOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium transition-colors hover:bg-accent"
        >
          <Share2 className="h-4 w-4" />
          {t('workspace.canvas.node.share')}
        </button>
        <DraftManageDrawer
          contentId={contentId}
          seed={seed}
          defaultOpen={autoOpenManage}
          identity={identity}
          onSaved={(saved) => {
            setIdentity(saved);
            // Cards elsewhere (Apps panel list, Mine shelf) re-read the row.
            emitWorkspaceItemsChanged();
          }}
        />
        <button
          type="button"
          onClick={() => setEditOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium transition-colors hover:bg-accent"
        >
          <Pencil className="h-4 w-4" />
          {t('actions.edit')}
        </button>
        {actions}
      </AppStoreHeader>
      {/* Edit = the EDIT.md coding-tool handoff on the owner's own app (no
          copy). NOT /content/<id> — that route is a share-preview deep link
          and renders a preview dialog, not an editor. */}
      <RemixWithDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        widgetName={identity.name}
        mode="edit"
        onRobutler={() => {}}
        editTarget={{
          contentId,
          folderId:
            typeof (metadata?.bundle as { folderId?: unknown } | undefined)?.folderId === 'string'
              ? ((metadata!.bundle as { folderId: string }).folderId)
              : null,
          displayName: identity.name,
        }}
      />
      <div className="mx-auto mt-4 max-w-2xl rounded-xl border border-dashed border-border/60 px-4 py-6 text-center">
        <MessagesSquare className="mx-auto mb-2 h-6 w-6 text-muted-foreground/70" />
        <p className="text-sm font-medium">{t('workspace.panels.giveAppHomeChannel')}</p>
        <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-muted-foreground">
          A channel is where people discuss your app, follow your updates, and you engage your users. Publishing
          creates one for you (boosted under <span className="font-mono">//apps</span>), or link one of your own.
        </p>
      </div>
      {shareOpen && (
        <ShareDialog
          kind="content"
          contentIds={[contentId]}
          onClose={() => setShareOpen(false)}
        />
      )}
    </>
  );
}
